import type { RecruitingCaseBrief } from '../../lib/types';

/**
 * The case brief, as a document to send a candidate.
 *
 * This is the one thing this app produces that goes to somebody OUTSIDE the
 * company, which changes two things about how it is built.
 *
 * THE ANSWER KEY MUST NEVER REACH IT. `RecruitingCaseBrief.InternalNote`
 * records the inconsistencies deliberately planted in the data and what a
 * strong candidate is expected to notice — mailing that with the brief would
 * destroy the exercise. This module takes the fields it needs one at a time
 * rather than spreading the brief object into a template, so a field added to
 * the brief later cannot silently start printing.
 *
 * IT IS PLAINER THAN THE SCORECARD ON PURPOSE. No badges, no score bands, no
 * internal vocabulary. A candidate reading it should see a clear exercise and
 * a company that is organised, not a page of somebody else's tooling.
 *
 * Rendered into a detached iframe and printed, the same way the scorecard is:
 * the alternative is fighting the app's layout with print overrides, and the
 * result reads as a screenshot of a web page rather than a document.
 *
 * Colours are literal hex because the iframe is a separate document and never
 * sees tokens.css. The comment beside each is the token it was quoted from.
 */

const NAVY = '#14395C'; /* --brand-900 */
const INK = '#2F4050'; /* --gray-700 */
const MUTED = '#546778'; /* --gray-500 */
const RULE = '#DCE3EA'; /* --gray-200 */
const WASH = '#F4F7FA'; /* --brand-50 */

const esc = (s: unknown): string =>
  String(s ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c,
  );

function styles(): string {
  return `
    @page { size: letter; margin: 18mm 16mm; }
    * { box-sizing: border-box; }
    body {
      margin: 0; color: ${INK}; background: #fff;
      font: 11pt/1.5 "Nunito Sans", "Segoe UI", Helvetica, Arial, sans-serif;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    h1, h2 { font-family: "Baloo 2", "Nunito Sans", Helvetica, Arial, sans-serif; }
    h1 { font-size: 22pt; font-weight: 800; color: ${NAVY}; margin: 0 0 2mm; letter-spacing: -.2pt; }
    h2 {
      font-size: 12pt; font-weight: 700; color: ${NAVY};
      margin: 9mm 0 3mm; padding-bottom: 1.5mm; border-bottom: 1px solid ${RULE};
    }
    .eyebrow {
      font-size: 8.5pt; font-weight: 700; letter-spacing: .12em; text-transform: uppercase;
      color: ${MUTED}; margin: 0 0 2mm;
    }
    .lede { font-size: 11.5pt; color: ${INK}; margin: 0 0 6mm; }

    /* The brief itself, in the words the candidate is given. Set apart so it
       reads as the instruction rather than as more prose about the exercise. */
    .brief {
      background: ${WASH}; border-left: 3px solid ${NAVY};
      padding: 5mm 6mm; margin: 0 0 4mm; font-size: 11pt;
    }

    table { width: 100%; border-collapse: collapse; margin: 0 0 3mm; }
    td { border-bottom: 1px solid ${RULE}; padding: 3mm 3mm 3mm 0; vertical-align: top; }
    tr:last-child td { border-bottom: 0; }
    td.k {
      width: 34mm; font-weight: 700; color: ${NAVY}; white-space: nowrap;
    }

    ol { margin: 0; padding-left: 6mm; }
    ol li { margin: 0 0 2.5mm; padding-left: 1mm; }

    .foot {
      margin-top: 10mm; padding-top: 3mm; border-top: 1px solid ${RULE};
      font-size: 9pt; color: ${MUTED};
    }
    /* Keep a section and its first rows together — a heading stranded at the
       foot of a page is the classic print-to-PDF tell. */
    h2 { break-after: avoid; }
    table, ol { break-inside: avoid; }
  `;
}

/**
 * Build the document. Takes the pieces it prints as separate arguments rather
 * than the whole brief, so nothing can arrive by accident — see the note above
 * about the answer key.
 */
export function buildCaseBriefHtml(brief: RecruitingCaseBrief, roleName: string): string {
  const body = brief.Body;
  const sections = body?.sections ?? [];
  const asks = body?.asks ?? [];

  const table = (rows: [string, string][]): string =>
    `<table>${rows
      .map(([k, v]) => `<tr><td class="k">${esc(k)}</td><td>${esc(v)}</td></tr>`)
      .join('')}</table>`;

  return `<!doctype html><html lang="en"><head><meta charset="utf-8">
<title>${esc(brief.Title)}</title><style>${styles()}</style></head><body>
  <p class="eyebrow">${esc(roleName)} · Interview exercise</p>
  <h1>${esc(brief.Title)}</h1>
  ${brief.Intro ? `<div class="brief">${esc(brief.Intro)}</div>` : ''}

  ${sections
    .map((s) => `<h2>${esc(s.heading)}</h2>${table(s.rows ?? [])}`)
    .join('')}

  ${
    asks.length
      ? `<h2>What to prepare</h2><ol>${asks.map((a) => `<li>${esc(a)}</li>`).join('')}</ol>`
      : ''
  }

  <p class="foot">
    Bring this in any format you like. You will not have enough information to be certain about
    everything — saying what you would verify, and why, is part of the exercise.
  </p>
</body></html>`;
}

/**
 * Render and hand to the browser's print dialog.
 *
 * Print-to-PDF rather than a generated file: it needs no dependency, it prints
 * what the reader sees, and "Save as PDF" is a destination in every modern
 * print dialog. Identical harness to the scorecard export.
 */
export function exportCaseBriefPdf(brief: RecruitingCaseBrief, roleName: string): void {
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
  document.body.appendChild(frame);

  const done = () => {
    if (frame.parentNode) frame.remove();
  };

  frame.onload = () => {
    const win = frame.contentWindow;
    if (!win) {
      done();
      return;
    }
    try {
      win.focus();
      win.onafterprint = done;
      win.print();
      /* Safari never fires onafterprint; clean up on a delay it cannot outlive. */
      setTimeout(done, 60_000);
    } catch {
      done();
    }
  };

  frame.srcdoc = buildCaseBriefHtml(brief, roleName);
}
